// Pure math for the free Investor Deal Analyzer.
// Zero imports from the paid calculator or FreeCalculator.js — deliberately
// re-implemented from the spec.

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

function mortgagePayment(principal, monthlyRate, numPayments) {
  if (principal <= 0) return 0;
  if (monthlyRate === 0) return principal / numPayments;
  const f = Math.pow(1 + monthlyRate, numPayments);
  return (principal * monthlyRate * f) / (f - 1);
}

function mortgageBalance(principal, monthlyRate, numPayments, monthsElapsed) {
  if (principal <= 0) return 0;
  if (monthsElapsed >= numPayments) return 0;
  const M = mortgagePayment(principal, monthlyRate, numPayments);
  if (monthlyRate === 0) return principal - M * monthsElapsed;
  const f = Math.pow(1 + monthlyRate, monthsElapsed);
  return principal * f - (M * (f - 1)) / monthlyRate;
}

export function computeDealMetrics(inputs) {
  const purchasePrice = num(inputs.purchase_price);
  const downPayment = num(inputs.down_payment);
  const interestRate = num(inputs.interest_rate);
  const loanTermYears = num(inputs.loan_term_years) || 30;

  const monthlyRent = num(inputs.monthly_rent);
  const otherMonthlyIncome = num(inputs.other_monthly_income);
  const vacancyRatePercent = num(inputs.vacancy_rate);

  const propertyTaxes = num(inputs.property_taxes);
  const insurance = num(inputs.insurance);
  const hoa = num(inputs.hoa);
  const propertyManagement = num(inputs.property_management);
  const maintenanceReserve = num(inputs.maintenance_reserve);
  const utilities = num(inputs.utilities);
  const otherExpenses = num(inputs.other_expenses);

  const appreciationRate = num(inputs.appreciation_rate);
  const exitCapRate = num(inputs.exit_cap_rate);

  const loanAmount = Math.max(0, purchasePrice - downPayment);
  const monthlyRate = interestRate / 100 / 12;
  const numPayments = loanTermYears * 12;
  const monthlyMortgage = mortgagePayment(loanAmount, monthlyRate, numPayments);

  const annualGrossIncome = (monthlyRent + otherMonthlyIncome) * 12;
  const annualVacancyLoss = annualGrossIncome * (vacancyRatePercent / 100);
  const effectiveGrossIncome = annualGrossIncome - annualVacancyLoss;

  const annualOperatingExpenses =
    (propertyTaxes + insurance + hoa + propertyManagement +
      maintenanceReserve + utilities + otherExpenses) * 12;

  const noi = effectiveGrossIncome - annualOperatingExpenses;
  const annualDebtService = monthlyMortgage * 12;
  const annualCashFlow = noi - annualDebtService;
  const monthlyCashFlow = annualCashFlow / 12;

  const capRate = purchasePrice > 0 ? (noi / purchasePrice) * 100 : 0;
  const cashInvested = downPayment > 0 ? downPayment : purchasePrice * 0.25;
  const cashOnCash = cashInvested > 0 ? (annualCashFlow / cashInvested) * 100 : 0;
  const dscr = annualDebtService > 0 ? noi / annualDebtService : null;
  const breakEvenOccupancy = annualGrossIncome > 0
    ? ((annualOperatingExpenses + annualDebtService) / annualGrossIncome) * 100
    : 0;

  const exitNOI = noi * Math.pow(1 + appreciationRate / 100, 5);
  const exitValue = exitCapRate > 0 ? exitNOI / (exitCapRate / 100) : 0;
  const totalCashFlow5yr = annualCashFlow * 5;
  const mortgageBalanceYr5 = mortgageBalance(loanAmount, monthlyRate, numPayments, 60);
  const netSaleProceeds = exitValue - mortgageBalanceYr5;
  const totalReturn = totalCashFlow5yr + netSaleProceeds - cashInvested;
  const totalROI5yr = cashInvested > 0 ? (totalReturn / cashInvested) * 100 : 0;
  const moic = cashInvested > 0
    ? (totalCashFlow5yr + netSaleProceeds) / cashInvested
    : 0;

  const warnings = [];
  if (purchasePrice > 0 && (capRate < 2 || capRate > 12)) {
    warnings.push({
      key: 'cap_rate',
      message: `Cap rate of ${capRate.toFixed(2)}% is unusual. Double-check your rent and expense inputs.`,
    });
  }
  if (dscr !== null && dscr < 1.0 && annualDebtService > 0) {
    warnings.push({
      key: 'dscr',
      message: 'DSCR below 1.0 means the property doesn\u2019t cover its debt service. Most lenders require 1.2+.',
    });
  }
  if (annualCashFlow < 0 && annualGrossIncome > 0) {
    warnings.push({
      key: 'negative_cash_flow',
      message: 'Cash flow is negative. You\u2019d be covering the shortfall out of pocket every month.',
    });
  }
  if (breakEvenOccupancy > 95 && annualGrossIncome > 0) {
    warnings.push({
      key: 'break_even',
      message: `Break-even occupancy is ${breakEvenOccupancy.toFixed(1)}%. Even a small vacancy period turns this into a money-losing deal.`,
    });
  }

  return {
    monthlyMortgage,
    monthlyCashFlow,
    annualCashFlow,
    noi,
    capRate,
    cashOnCash,
    dscr,
    breakEvenOccupancy,
    exitValue,
    totalROI5yr,
    moic,
    warnings,
    _debug: {
      loanAmount, annualGrossIncome, effectiveGrossIncome,
      annualOperatingExpenses, annualDebtService, cashInvested,
      mortgageBalanceYr5, netSaleProceeds,
    },
  };
}

export function fmtUSD(num_, opts = {}) {
  const { sign = false, cents = false } = opts;
  if (num_ == null || Number.isNaN(num_)) return '—';
  const abs = Math.abs(num_);
  const str =
    '$' +
    abs.toLocaleString('en-US', {
      minimumFractionDigits: cents ? 2 : 0,
      maximumFractionDigits: cents ? 2 : 0,
    });
  if (sign) return (num_ >= 0 ? '+' : '−') + str;
  return (num_ < 0 ? '−' : '') + str;
}

export function fmtPct(n, decimals = 2) {
  if (n == null || Number.isNaN(n)) return '—';
  return n.toFixed(decimals) + '%';
}

export function fmtMoic(n) {
  if (n == null || Number.isNaN(n) || !Number.isFinite(n)) return '—';
  return n.toFixed(2) + 'x';
}

export function fmtDscr(n) {
  if (n == null || Number.isNaN(n) || !Number.isFinite(n)) return '—';
  return n.toFixed(2);
}
